/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Lock,
  RefreshCw,
  Save,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { formatDateTimeObject } from '@/lib/time'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  StatusBadge,
  dotColorMap,
  textColorMap,
  type StatusVariant,
} from '@/components/status-badge'
import {
  getModelFieldSyncSettings,
  previewModelFieldSync,
  runModelFieldSync,
  setModelFieldSyncLock,
  updateModelFieldSyncSettings,
} from '../api'
import { modelsQueryKeys } from '../lib'
import { normalizeCapabilities } from '../lib/capability-edit'
import { ModelCapabilityEditDialog } from './dialogs/model-capability-edit-dialog'
import type {
  ModelFieldSyncItem,
  ModelFieldSyncLocale,
  ModelFieldSyncResult,
} from '../types'

const LOCALE_OPTIONS: Array<{ value: ModelFieldSyncLocale; label: string }> = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
]

function formatNumber(value?: number | null) {
  if (value == null) return null
  return value.toLocaleString()
}

function formatBoolean(value?: boolean | null) {
  if (value == null) return null
  return value ? 'Yes' : 'No'
}

function formatLastRun(timestamp?: number) {
  if (!timestamp) return '-'
  return formatDateTimeObject(new Date(timestamp * 1000))
}

function formatSkippedReason(
  reason: string | undefined,
  t: ReturnType<typeof useTranslation>['t']
) {
  switch (reason) {
    case 'not_found_in_source':
      return t('Not found in source')
    default:
      return reason || t('Skipped')
  }
}

function SummaryValue({
  label,
  value,
}: {
  label: string
  value: string | number
}) {
  return (
    <div className='min-w-0 rounded-lg border px-3 py-2'>
      <div className='text-muted-foreground text-xs'>{label}</div>
      <div className='truncate text-base font-medium'>{value}</div>
    </div>
  )
}

function getCapabilityLabel(value: string, t: ReturnType<typeof useTranslation>['t']) {
  switch (value.toLowerCase()) {
    case 'tools':
    case 'tool':
      return t('Tool calling')
    case 'reasoning':
      return t('Reasoning')
    case 'files':
    case 'file':
      return t('File input')
    case 'audio':
      return t('Audio')
    case 'open weights':
    case 'open_weights':
      return t('Open weights')
    case 'text':
      return t('Text')
    case 'image':
    case 'vision':
      return t('Vision')
    default:
      return value
  }
}

function getModalityLabel(value: string, t: ReturnType<typeof useTranslation>['t']) {
  switch (value.toLowerCase()) {
    case 'text':
      return t('Text')
    case 'image':
    case 'vision':
      return t('Image')
    case 'file':
    case 'files':
      return t('File')
    case 'audio':
      return t('Audio')
    case 'video':
      return t('Video')
    default:
      return value
  }
}

function getModalities(
  item: ModelFieldSyncItem,
  direction: 'input' | 'output'
) {
  const modalities = item.capabilities?.modalities
  if (!modalities || typeof modalities !== 'object') return []
  const values = (modalities as { input?: unknown; output?: unknown })[
    direction
  ]
  if (!Array.isArray(values)) return []
  return values
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter(Boolean)
}

function renderModalityBadges(
  values: string[],
  t: ReturnType<typeof useTranslation>['t']
) {
  if (values.length === 0) {
    return <span className='text-muted-foreground'>-</span>
  }
  return (
    <div className='inline-flex flex-wrap gap-1 align-middle'>
      {values.map((value) => {
        const label = getModalityLabel(value, t)
        return (
          <StatusBadge key={value} label={label} autoColor={label} size='sm' />
        )
      })}
    </div>
  )
}

function addCapability(
  labels: Set<string>,
  raw: unknown,
  t: ReturnType<typeof useTranslation>['t']
) {
  if (typeof raw !== 'string') return
  const trimmed = raw.trim()
  if (!trimmed) return

  const normalized = trimmed.toLowerCase()
  if (
    normalized === 'text' ||
    normalized === 'image' ||
    normalized === 'vision' ||
    normalized === 'tools' ||
    normalized === 'tool' ||
    normalized === 'reasoning' ||
    normalized === 'files' ||
    normalized === 'file' ||
    normalized.endsWith('k') ||
    normalized.endsWith('m')
  ) {
    return
  }

  labels.add(getCapabilityLabel(trimmed, t))
}

function getOtherCapabilities(
  item: ModelFieldSyncItem,
  t: ReturnType<typeof useTranslation>['t']
) {
  const labels = new Set<string>()

  item.tags
    ?.split(',')
    .forEach((tag) => addCapability(labels, tag, t))

  const capabilities = item.capabilities
  const modalities = capabilities?.modalities
  if (modalities && typeof modalities === 'object') {
    const input = (modalities as { input?: unknown }).input
    const output = (modalities as { output?: unknown }).output
    if (Array.isArray(input)) input.forEach((value) => addCapability(labels, value, t))
    if (Array.isArray(output)) output.forEach((value) => addCapability(labels, value, t))
  }

  const features = capabilities?.features
  if (features && typeof features === 'object' && !Array.isArray(features)) {
    Object.keys(features).forEach((key) => addCapability(labels, key, t))
  }

  const tasks = capabilities?.tasks
  if (tasks && typeof tasks === 'object') {
    Object.keys(tasks).forEach((key) => addCapability(labels, key, t))
  }

  const legacyFeatures = capabilities?.features
  if (Array.isArray(legacyFeatures)) {
    legacyFeatures.forEach((value) => addCapability(labels, value, t))
  }

  return [...labels]
}

function renderCapabilityBadges(values: string[]) {
  if (values.length === 0) {
    return <span className='text-muted-foreground'>-</span>
  }
  return (
    <div className='flex flex-wrap gap-1'>
      {values.map((value) => (
        <StatusBadge key={value} label={value} autoColor={value} size='sm' />
      ))}
    </div>
  )
}

function getSourceTypeLabel(value: string) {
  switch (value) {
    case 'litellm':
      return 'LiteLLM'
    case 'models_dev':
      return 'models.dev'
    case 'openrouter':
      return 'OpenRouter'
    default:
      return value
  }
}

function renderSourceBadges(
  values: string[] | undefined,
  t: ReturnType<typeof useTranslation>['t']
) {
  if (!values || values.length === 0) {
    return <span className='text-muted-foreground'>{t('No source')}</span>
  }
  return (
    <div className='inline-flex flex-wrap gap-1 align-middle'>
      {values.map((value) => {
        const label = getSourceTypeLabel(value)
        return (
          <StatusBadge key={value} label={label} autoColor={label} size='sm' />
        )
      })}
    </div>
  )
}

function renderModelList(values: string[]) {
  if (values.length === 0)
    return <span className='text-muted-foreground'>-</span>
  return (
    <div className='max-h-20 max-w-[280px] space-y-1 overflow-auto pr-1'>
      {values.map((value) => (
        <div key={value} className='truncate text-sm leading-5'>
          {value}
        </div>
      ))}
    </div>
  )
}

// Where a displayed field value came from, so the table can badge it and users
// can tell "the upstream source confirmed this" apart from "we kept your stored
// value because upstream was silent" — the two used to be indistinguishable and
// read as a data conflict against the edit dialog (which shows the DB value).
type FieldSource = 'upstream' | 'stored' | 'manual'

interface ResolvedField<T> {
  value: T | null
  source: FieldSource | null
}

// Resolve one scalar field to its effective value + provenance.
// - Locked rows are authoritative from the DB snapshot ('manual'). A null there
//   means the user explicitly unset it — we must NOT fall back to upstream.
// - Unlocked rows prefer the live upstream value; when upstream is silent we
//   fall back to the stored DB value ('stored'). Sync's "!= nil" guard keeps
//   that DB value anyway, so this is exactly what the field will be post-sync —
//   and it matches what the edit dialog shows, killing the false "conflict".
function resolveField<T>(
  upstreamValue: T | null | undefined,
  storedValue: T | null | undefined,
  locked: boolean
): ResolvedField<T> {
  if (locked) {
    const value = storedValue ?? null
    return { value, source: value == null ? null : 'manual' }
  }
  if (upstreamValue != null) return { value: upstreamValue, source: 'upstream' }
  if (storedValue != null) return { value: storedValue, source: 'stored' }
  return { value: null, source: null }
}

interface ResolvedDisplayItem {
  // Item carrying the *effective* capabilities blob, fed to the capability /
  // modality readers so they follow the same fallback the scalar columns use.
  item: ModelFieldSyncItem
  vision: ResolvedField<boolean>
  context: ResolvedField<number>
  output: ResolvedField<number>
}

function resolveDisplayItem(item: ModelFieldSyncItem): ResolvedDisplayItem {
  const locked = Boolean(item.field_sync_locked)
  const stored = item.stored
  const vision = resolveField(
    item.supports_vision,
    stored?.supports_vision,
    locked
  )
  const context = resolveField(
    item.context_window,
    stored?.context_window,
    locked
  )
  const output = resolveField(
    item.max_output_tokens,
    stored?.max_output_tokens,
    locked
  )

  // Capabilities follow the same rule: locked -> stored; unlocked -> upstream,
  // falling back to stored when upstream carries none.
  const upstreamCaps = normalizeCapabilities(item.capabilities)
  const storedCaps = normalizeCapabilities(stored?.capabilities)
  const capabilities =
    !locked && Object.keys(upstreamCaps).length > 0 ? upstreamCaps : storedCaps

  return {
    item: { ...item, capabilities },
    vision,
    context,
    output,
  }
}

const FIELD_SOURCE_VARIANT: Record<FieldSource, StatusVariant> = {
  upstream: 'success',
  stored: 'info',
  manual: 'neutral',
}

function FieldSourceBadge({
  source,
  t,
}: {
  source: FieldSource
  t: ReturnType<typeof useTranslation>['t']
}) {
  const variant = FIELD_SOURCE_VARIANT[source]
  const label =
    source === 'upstream'
      ? t('From upstream')
      : source === 'stored'
        ? t('From stored')
        : t('Manual')
  const title =
    source === 'upstream'
      ? t('From upstream hint')
      : source === 'stored'
        ? t('From stored hint')
        : t('Manual hint')
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-xs',
        textColorMap[variant]
      )}
    >
      {source === 'manual' ? (
        <Lock className='size-3 shrink-0' aria-hidden='true' />
      ) : (
        <span
          className={cn(
            'inline-block size-1.5 shrink-0 rounded-full',
            dotColorMap[variant]
          )}
          aria-hidden='true'
        />
      )}
      {label}
    </span>
  )
}

// A scalar cell: effective value + a badge saying where it came from. When
// nothing exists in either upstream or the DB, the value is genuinely unset and
// the edit dialog agrees, so the plain "Source not provided" placeholder stays.
function renderFieldCell(
  value: string | null,
  source: FieldSource | null,
  t: ReturnType<typeof useTranslation>['t']
) {
  if (!value) {
    return (
      <span className='text-muted-foreground text-xs'>
        {t('Source not provided')}
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1.5'>
      <span>{value}</span>
      {source ? <FieldSourceBadge source={source} t={t} /> : null}
    </span>
  )
}

function ModelFieldSyncTable({
  items,
  onLockChanged,
  onEdit,
}: {
  items: ModelFieldSyncItem[]
  onLockChanged?: () => void
  onEdit?: (item: ModelFieldSyncItem) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const [pendingLock, setPendingLock] = useState<string | null>(null)

  const toggleRow = (modelName: string) => {
    setExpandedRows((current) => ({
      ...current,
      [modelName]: !current[modelName],
    }))
  }

  const lockMutation = useMutation({
    mutationFn: (vars: { model_name: string; locked: boolean }) =>
      setModelFieldSyncLock(vars),
    onMutate: (vars) => setPendingLock(vars.model_name),
    onSuccess: (res, vars) => {
      if (!res.success) {
        toast.error(res.message || t('Failed to update sync lock'))
        return
      }
      toast.success(
        vars.locked ? t('Model sync locked') : t('Model sync unlocked')
      )
      // Drop any stale "last run" snapshot so the row reflects the fresh
      // preview (which now carries the updated lock state).
      onLockChanged?.()
      queryClient.invalidateQueries({ queryKey: modelsQueryKeys.fieldSync() })
    },
    onError: (error: Error) => {
      toast.error(error.message || t('Failed to update sync lock'))
    },
    onSettled: () => setPendingLock(null),
  })

  if (items.length === 0) {
    return (
      <Empty className='min-h-48 rounded-lg border'>
        <EmptyTitle>{t('No channel models found')}</EmptyTitle>
        <EmptyDescription>
          {t('Add models to channels before running model field sync.')}
        </EmptyDescription>
      </Empty>
    )
  }

  return (
    <div className='rounded-lg border'>
      <ScrollArea className='w-full'>
        <Table className='min-w-[980px]'>
          <TableHeader>
            <TableRow>
              <TableHead className='w-10' />
              <TableHead>{t('Upstream model')}</TableHead>
              <TableHead>{t('Display models')}</TableHead>
              <TableHead>{t('Status')}</TableHead>
              <TableHead>{t('Vision')}</TableHead>
              <TableHead>{t('Context')}</TableHead>
              <TableHead>{t('Output limit')}</TableHead>
              <TableHead className='w-[132px] text-left'>
                {t('Special capabilities')}
              </TableHead>
              <TableHead className='w-[96px] text-left'>
                {t('Sync lock')}
              </TableHead>
              <TableHead className='w-[72px] text-left' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const expanded = Boolean(expandedRows[item.model_name])
              const hasFields = Boolean(item.fields && item.fields.length > 0)
              const resolved = resolveDisplayItem(item)
              const displayItem = resolved.item
              const otherCapabilities = getOtherCapabilities(displayItem, t)
              const inputModalities = getModalities(displayItem, 'input')
              const outputModalities = getModalities(displayItem, 'output')
              return (
                <Fragment key={item.model_name}>
                  <TableRow className='[&>td]:py-2'>
                    <TableCell className='align-top'>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6'
                        onClick={() => toggleRow(item.model_name)}
                        aria-expanded={expanded}
                        aria-label={t('Toggle details')}
                      >
                        {expanded ? (
                          <ChevronDown className='h-4 w-4' />
                        ) : (
                          <ChevronRight className='h-4 w-4' />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className='max-w-[220px] align-top font-medium break-all'>
                      {item.model_name}
                    </TableCell>
                    <TableCell className='align-top'>
                      {renderModelList(item.display_models)}
                    </TableCell>
                    <TableCell className='align-top'>
                      {item.matched ? (
                        hasFields ? (
                          <StatusBadge label={t('Ready')} variant='success' />
                        ) : (
                          <StatusBadge
                            label={t('Matched, no fields')}
                            variant='neutral'
                          />
                        )
                      ) : (
                        <StatusBadge
                          label={formatSkippedReason(item.skipped_reason, t)}
                          variant='warning'
                        />
                      )}
                    </TableCell>
                    <TableCell className='align-top whitespace-nowrap'>
                      {renderFieldCell(
                        formatBoolean(resolved.vision.value),
                        resolved.vision.source,
                        t
                      )}
                    </TableCell>
                    <TableCell className='align-top whitespace-nowrap'>
                      {renderFieldCell(
                        formatNumber(resolved.context.value),
                        resolved.context.source,
                        t
                      )}
                    </TableCell>
                    <TableCell className='align-top whitespace-nowrap'>
                      {renderFieldCell(
                        formatNumber(resolved.output.value),
                        resolved.output.source,
                        t
                      )}
                    </TableCell>
                    <TableCell className='w-[132px] align-top text-left whitespace-nowrap'>
                      {otherCapabilities.length > 0 ? (
                        <Button
                          variant='ghost'
                          size='sm'
                          className='text-muted-foreground hover:text-foreground h-7 justify-start gap-1 px-0 text-xs hover:bg-transparent hover:underline'
                          onClick={() => toggleRow(item.model_name)}
                          aria-expanded={expanded}
                          aria-label={t('Toggle details')}
                        >
                          {expanded ? t('Collapse') : t('Expand to view')}
                          {expanded ? (
                            <ChevronDown className='h-3.5 w-3.5' />
                          ) : (
                            <ChevronRight className='h-3.5 w-3.5' />
                          )}
                        </Button>
                      ) : (
                        <span className='text-muted-foreground inline-flex h-7 items-center text-xs'>
                          {t('No special capabilities')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className='align-top'>
                      <div
                        className='flex h-7 items-center gap-2'
                        title={
                          item.has_local_meta
                            ? undefined
                            : t('Create model metadata first to lock it')
                        }
                      >
                        <Switch
                          checked={item.field_sync_locked}
                          disabled={
                            !item.has_local_meta ||
                            pendingLock === item.model_name
                          }
                          onCheckedChange={(checked) =>
                            lockMutation.mutate({
                              model_name: item.model_name,
                              locked: checked,
                            })
                          }
                          aria-label={t('Lock model from sync')}
                        />
                        {item.field_sync_locked ? (
                          <Lock className='text-muted-foreground h-3.5 w-3.5' />
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className='align-top'>
                      <Button
                        variant='outline'
                        size='sm'
                        className='h-7 px-2 text-xs'
                        onClick={() => onEdit?.(item)}
                      >
                        {t('Edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow>
                      <TableCell />
                      <TableCell
                        colSpan={9}
                        className='bg-muted/50 px-4 py-3'
                      >
                        <div className='flex flex-wrap items-start gap-x-8 gap-y-3 text-sm'>
                          <div className='flex min-w-[280px] items-start gap-2'>
                            <span className='shrink-0 font-medium'>
                              {t('Source')}:
                            </span>
                            {renderSourceBadges(item.source_types, t)}
                          </div>
                          <div className='max-w-[560px] min-w-[260px]'>
                            <span className='font-medium'>
                              {t('Source tags')}:
                            </span>{' '}
                            <span className='text-muted-foreground break-words'>
                              {item.tags || '-'}
                            </span>
                          </div>
                          <div className='flex min-w-[220px] items-start gap-2'>
                            <span className='shrink-0 font-medium'>
                              {t('Input modalities')}:
                            </span>
                            {renderModalityBadges(inputModalities, t)}
                          </div>
                          <div className='flex min-w-[220px] items-start gap-2'>
                            <span className='shrink-0 font-medium'>
                              {t('Output modalities')}:
                            </span>
                            {renderModalityBadges(outputModalities, t)}
                          </div>
                          <div className='flex min-w-[260px] items-start gap-2'>
                            <span className='shrink-0 font-medium'>
                              {t('Special capabilities')}:
                            </span>
                            {renderCapabilityBadges(otherCapabilities)}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
        <ScrollBar orientation='horizontal' />
      </ScrollArea>
    </div>
  )
}

export function ModelFieldSyncPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [autoEnabled, setAutoEnabled] = useState(false)
  const [intervalHours, setIntervalHours] = useState(24)
  const [locale, setLocale] = useState<ModelFieldSyncLocale>('zh-CN')
  const [latestRun, setLatestRun] = useState<ModelFieldSyncResult | null>(null)
  const [editItem, setEditItem] = useState<ModelFieldSyncItem | null>(null)

  const settingsQuery = useQuery({
    queryKey: modelsQueryKeys.fieldSyncSettings(),
    queryFn: getModelFieldSyncSettings,
  })

  useEffect(() => {
    const settings = settingsQuery.data?.data
    if (!settings) return
    setAutoEnabled(settings.auto_enabled)
    setIntervalHours(settings.interval_hours || 24)
    setLocale(settings.locale || 'zh-CN')
  }, [settingsQuery.data?.data])

  const previewQuery = useQuery({
    queryKey: modelsQueryKeys.fieldSyncPreview(locale),
    queryFn: () => previewModelFieldSync({ locale }),
    enabled: settingsQuery.isSuccess,
  })

  const saveSettingsMutation = useMutation({
    mutationFn: () =>
      updateModelFieldSyncSettings({
        auto_enabled: autoEnabled,
        interval_hours: intervalHours,
        locale,
      }),
    onSuccess: (res) => {
      if (!res.success) {
        toast.error(res.message || t('Failed to save settings'))
        return
      }
      toast.success(t('Settings saved'))
      queryClient.invalidateQueries({
        queryKey: modelsQueryKeys.fieldSyncSettings(),
      })
      queryClient.invalidateQueries({ queryKey: modelsQueryKeys.fieldSync() })
    },
    onError: (error: Error) => {
      toast.error(error.message || t('Failed to save settings'))
    },
  })

  const runMutation = useMutation({
    mutationFn: () => runModelFieldSync({ locale }),
    onSuccess: (res) => {
      if (!res.success || !res.data) {
        toast.error(res.message || t('Model field sync failed'))
        return
      }
      setLatestRun(res.data)
      toast.success(t('Model field sync completed'))
      queryClient.invalidateQueries({ queryKey: modelsQueryKeys.fieldSync() })
      queryClient.invalidateQueries({ queryKey: modelsQueryKeys.lists() })
    },
    onError: (error: Error) => {
      toast.error(error.message || t('Model field sync failed'))
    },
  })

  const result = latestRun || previewQuery.data?.data
  const settings = settingsQuery.data?.data
  const sourceURLs =
    result?.source?.sources?.map((source) => `${source.name}: ${source.url}`) ||
    (result?.source?.models_url ? [result.source.models_url] : [])
  const isLoading = settingsQuery.isLoading || previewQuery.isLoading
  const isBusy =
    saveSettingsMutation.isPending ||
    runMutation.isPending ||
    previewQuery.isFetching

  const summary = useMemo(
    () => ({
      matched: result?.matched_models ?? 0,
      updated: (result?.updated_models ?? 0) + (result?.created_models ?? 0),
      skipped: result?.skipped_models ?? 0,
      total: result?.items?.length ?? 0,
    }),
    [result]
  )

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <div className='flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'>
            <div className='space-y-1'>
              <CardTitle>{t('Model Field Sync')}</CardTitle>
              <CardDescription>
                {t(
                  'Sync capability fields for models already configured on channels.'
                )}
              </CardDescription>
            </div>
            <div className='flex flex-wrap gap-2'>
              <Button
                variant='outline'
                onClick={() => previewQuery.refetch()}
                disabled={isBusy}
                size='sm'
              >
                {previewQuery.isFetching ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
                {t('Preview')}
              </Button>
              <Button
                onClick={() => runMutation.mutate()}
                disabled={isBusy}
                size='sm'
              >
                {runMutation.isPending ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
                {t('Sync now')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
            <SummaryValue label={t('Matched')} value={summary.matched} />
            <SummaryValue label={t('Will update')} value={summary.updated} />
            <SummaryValue label={t('Skipped')} value={summary.skipped} />
            <SummaryValue label={t('Channel models')} value={summary.total} />
          </div>

          <div className='grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end'>
            <div className='space-y-2'>
              <Label htmlFor='model-field-sync-locale'>{t('Language')}</Label>
              <NativeSelect
                id='model-field-sync-locale'
                className='w-full'
                value={locale}
                onChange={(event) =>
                  setLocale(event.target.value as ModelFieldSyncLocale)
                }
                disabled={isBusy}
              >
                {LOCALE_OPTIONS.map((option) => (
                  <NativeSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='model-field-sync-interval'>
                {t('Auto sync interval (hours)')}
              </Label>
              <Input
                id='model-field-sync-interval'
                type='number'
                min={1}
                value={intervalHours}
                onChange={(event) =>
                  setIntervalHours(Math.max(1, Number(event.target.value) || 1))
                }
                disabled={isBusy}
              />
            </div>
            <div className='flex items-center justify-between gap-3 rounded-lg border px-3 py-2'>
              <div className='min-w-0'>
                <Label htmlFor='model-field-sync-auto'>{t('Auto sync')}</Label>
                <p className='text-muted-foreground text-xs'>
                  {t('Runs on the master node only.')}
                </p>
              </div>
              <Switch
                id='model-field-sync-auto'
                checked={autoEnabled}
                onCheckedChange={(value) => setAutoEnabled(!!value)}
                disabled={isBusy}
              />
            </div>
            <Button
              variant='outline'
              onClick={() => saveSettingsMutation.mutate()}
              disabled={isBusy}
            >
              {saveSettingsMutation.isPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Save className='h-4 w-4' />
              )}
              {t('Save')}
            </Button>
          </div>

          <div className='text-muted-foreground grid gap-2 text-xs lg:grid-cols-2'>
            <div className='min-w-0 space-y-1'>
              <div>{t('Sources')}:</div>
              {sourceURLs.length > 0 ? (
                sourceURLs.map((sourceURL) => (
                  <div key={sourceURL} className='truncate'>
                    {sourceURL}
                  </div>
                ))
              ) : (
                <div>-</div>
              )}
            </div>
            <div className='truncate lg:text-right'>
              {t('Last run')}: {formatLastRun(settings?.last_run_at)} ·{' '}
              {settings?.last_run_status || '-'} ·{' '}
              {settings?.last_run_message || '-'}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('Current model field status')}</CardTitle>
          <CardDescription>
            {t(
              'Each row is one current model resolved from configured channels. The values shown are the latest preview or sync result.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex min-h-48 items-center justify-center'>
              <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
            </div>
          ) : (
            <ModelFieldSyncTable
              items={result?.items || []}
              onLockChanged={() => setLatestRun(null)}
              onEdit={setEditItem}
            />
          )}
        </CardContent>
      </Card>

      <ModelCapabilityEditDialog
        open={!!editItem}
        item={editItem}
        onOpenChange={(open) => {
          if (!open) setEditItem(null)
        }}
        onSaved={() => {
          toast.success(t('Saved'))
          setLatestRun(null)
          queryClient.invalidateQueries({
            queryKey: modelsQueryKeys.fieldSync(),
          })
          queryClient.invalidateQueries({ queryKey: modelsQueryKeys.lists() })
          setEditItem(null)
        }}
      />
    </div>
  )
}
